import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import ToastWrapper from '@common/helpers/toastWrapper';
import { useTranslation } from '@common/hooks/translation';
import PaymentsService from '@app/dev/services/payments';

import {
  MPESA_PLAYER_STORAGE_KEY,
  PAYMENT_STATUS_PATHS,
  PENDING_PAYMENT_STORAGE_KEY,
} from '../../constants/paymentRedirect';

// Standalone return handler for the KesPay deposit/withdraw flow. KesPay
// now redirects the whole tab (no iframe) to one of `/payment-*` after the
// user completes — or cancels — the transaction. This route sits at the
// top of `routeDefinitions` outside `<Layout>`, so it mounts before any
// chrome and can act on the outcome immediately.
//
// Responsibilities (in order):
//   1. Re-apply the stashed M-Pesa playerInfo via PaymentsService so the
//      Redux state that was lost on the full-page redirect is restored
//      and the user is NOT logged out of their M-Pesa session.
//   2. Show a success / failure / cancellation toast tailored to the
//      transaction type ("Deposit" vs "Withdrawal"), based on which of
//      the four PAYMENT_STATUS_PATHS we landed on.
//   3. Navigate back to the application root so the user resumes on a
//      proper page rather than the empty callback URL.

const PATH_TO_OUTCOME = {
  [PAYMENT_STATUS_PATHS.success]: 'success',
  [PAYMENT_STATUS_PATHS.fail]: 'fail',
  [PAYMENT_STATUS_PATHS.cancel]: 'cancel',
  [PAYMENT_STATUS_PATHS.pending]: 'pending',
};

const TOAST_CONFIG = {
  success: { kind: 'success', verb: 'successful' },
  fail: { kind: 'error', verb: 'failed' },
  cancel: { kind: 'error', verb: 'cancelled' },
  pending: { kind: 'info', verb: 'pending' },
};

const normalizePath = (pathname) => {
  if (!pathname) return pathname;
  return pathname.length > 1 && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;
};

const PaymentCallbackBridge = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const t = useTranslation();
  // Guard so the effect's restore + toast + navigate only fires once even
  // under StrictMode double-invocation.
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;

    const outcome = PATH_TO_OUTCOME[normalizePath(pathname)];

    // Restore the M-Pesa player session that was stashed before the
    // redirect. PaymentsService.setPlayerInfo dispatches the
    // setPlayerPaymentInfo action and persists to the cashdesk middleware
    // — same code path used by the normal player-info flow.
    try {
      const raw = sessionStorage.getItem(MPESA_PLAYER_STORAGE_KEY);
      if (raw) {
        const playerInfo = JSON.parse(raw);
        if (playerInfo && playerInfo.PersonalId) {
          PaymentsService.setPlayerInfo(playerInfo);
        }
      }
    } catch (_) {
      // Corrupted JSON / disabled storage — non-fatal; we still toast and
      // navigate so the user gets back to a usable screen.
    }

    // Pick the transaction verb from the stashed pending-payment record
    // (set by mpesaDeposit / mpesaWithdraw). Falls back to "Transaction"
    // if the record is missing (e.g. direct visit to the callback URL).
    let label = 'Transaction';
    try {
      const raw = sessionStorage.getItem(PENDING_PAYMENT_STORAGE_KEY);
      if (raw) {
        const ctx = JSON.parse(raw);
        if (ctx?.type === 'withdraw') label = 'Withdrawal';
        else if (ctx?.type === 'deposit') label = 'Deposit';
      }
    } catch (_) { /* see above */ }

    const config = outcome && TOAST_CONFIG[outcome];
    if (config) {
      ToastWrapper(config.kind, t(`${label} ${config.verb}`));
    }

    // Clear stashes so a follow-up reload doesn't replay the toast.
    try {
      sessionStorage.removeItem(PENDING_PAYMENT_STORAGE_KEY);
      sessionStorage.removeItem(MPESA_PLAYER_STORAGE_KEY);
    } catch (_) { /* see above */ }

    // Back to the app root. `replace: true` so the callback URL doesn't
    // sit in the back-button history.
    navigate('/', { replace: true });
  }, [pathname, navigate, t]);

  return null;
};

export default PaymentCallbackBridge;
